
import { defineComponent, reactive, watch, ref, nextTick, toRefs } from 'vue';
import LangSelect from '@/components/lang_select/Index.vue';
import FindKey from '@/components/find-key/Index.vue';
import { isValidUsername } from '@/utils/validate';
import { useRoute, LocationQuery, useRouter } from 'vue-router';
import { useStore } from '@/store';
import { UserActionTypes } from '@/store/modules/user/action-types';
import Agreement from './Agreement.vue';
import i18n from '@/locales/index';
import { getRemember } from '@/utils/cookies';
import { ElMessage } from 'element-plus';
import https from '@/utils/https';
import { RootObject } from '@/model/rootObject';
import { LoginModel } from './model/loginModel';
import { ContentType, Method } from 'axios-mapper';
import Logo from './logo.vue';
export default defineComponent({
    components: {
        LangSelect,
        Agreement,
        FindKey,
        Logo
    },
    setup() {
        const userNameRef = ref(null);
        const passwordRef = ref(null);
        const loginFormRef = ref(null);
        const router = useRouter();
        const route = useRoute();
        const store = useStore();

        //
        const loginMethod = ref<string>('PASSWORD');
        const loginEmailFormRef = ref(null);
        const validateEmail = (rule, value, callback) => {
            if (value === '') callback(new Error(i18n.global.t('email.placeHolder')));
            else if (!/^[a-zA-Z0-9\u4e00-\u9fa5\u00C0-\u024F](([_-]|\.)?[a-zA-Z0-9\u4e00-\u9fa5\u00C0-\u024F]+)+@[a-zA-Z0-9]+([_-]?[a-zA-Z0-9]+)?(\.[a-zA-Z0-9]+([_-]?[a-zA-Z0-9]+)?)+$/.test(value)) callback(new Error(i18n.global.t('user.emailRule2')));
            else callback();
        };
        // 邮箱登录步骤1【STEP1】 输入信息进行验证
        // 邮箱登录步骤2【STEP2】 需要输入验证码验证
        const loginEmailStep = ref('STEP1');
        const emailVerifyCode = ref('');
        const isRemember = ref(false);
        const countdownTips = ref('');
        let timer = 0;
        const leftTime = ref(0);
        //
        // const { t } = useI18n()
        const state = reactive({
            loginForm: {
                username: '',
                password: '',
                company_id: ''
            },
            loginRules: {
                company_id: [
                    {
                        required: true,
                        message: i18n.global.t('login.tip_company'),
                        trigger: 'blur'
                    }
                ],
                username: [
                    {
                        required: true,
                        message: i18n.global.t('user.placeHolder'),
                        trigger: 'blur'
                    }
                ],
                password: [
                    {
                        required: true,
                        message: i18n.global.t('login.tip_pwd'),
                        trigger: 'blur'
                    }
                ]
            },
            passwordType: 'password',
            loading: false,
            showDialog: false,
            capsTooltip: false,
            redirect: '',
            otherQuery: {},
            loginEmailForm: {
                email: '',
                password: '',
                company_id: '',
                need: false,
                code: '',
                remember: false
            },
            loginEmailRules: {
                company_id: [
                    {
                        required: true,
                        message: i18n.global.t('login.tip_company'),
                        trigger: 'blur'
                    }
                ],
                email: [{ required: true, validator: validateEmail, trigger: 'blur' }],
                password: [
                    {
                        required: true,
                        message: i18n.global.t('login.tip_pwd'),
                        trigger: 'blur'
                    }
                ]
            }
        });

        const methods = reactive({
            changeLoginType: (type: string) => {
                state.loginEmailForm = {
                    email: '',
                    password: '',
                    company_id: '',
                    need: false,
                    code: '',
                    remember: false
                };
                state.loginForm = {
                    username: '',
                    password: '',
                    company_id: ''
                };
                loginMethod.value = type;
            },
            validateUsername: (rule: any, value: string, callback: Function) => {
                if (!isValidUsername(value)) {
                    callback(new Error('Please enter the correct user name'));
                } else {
                    callback();
                }
            },
            validatePassword: (rule: any, value: string, callback: Function) => {
                if (value.length < 6) {
                    callback(new Error('The password can not be less than 6 digits'));
                } else {
                    callback();
                }
            },
            checkCapslock: (e: KeyboardEvent) => {
                const { key } = e;
                state.capsTooltip = key !== null && key.length === 1 && key >= 'A' && key <= 'Z';
            },
            showPwd: () => {
                if (state.passwordType === 'password') {
                    state.passwordType = '';
                } else {
                    state.passwordType = 'password';
                }
                nextTick(() => {
                    (passwordRef.value as any).focus();
                });
            },
            handleLogin: () => {
                (loginFormRef.value as any).validate(async (valid: boolean) => {
                    if (valid) {
                        state.loading = true;
                        await store.dispatch(UserActionTypes.ACTION_LOGIN, state.loginForm);
                        router
                            .push({
                                path: state.redirect || '/',
                                query: state.otherQuery
                            })
                            .catch((err) => {
                                console.warn(err);
                            });
                        // Just to simulate the time of the request
                        setTimeout(() => {
                            state.loading = false;
                        }, 0.5 * 1000);
                    } else {
                        return false;
                    }
                });
            },
            handleEmailLogin: () => {
                (loginEmailFormRef.value as any).validate(async (valid: boolean) => {
                    if (!valid) return false;
                    https(false)
                        .request<RootObject<LoginModel>>(
                            'email_login',
                            Method.POST,
                            {
                                email: state.loginEmailForm.email,
                                company_id: state.loginEmailForm.company_id,
                                password: state.loginEmailForm.password,
                                need: getRemember(state.loginEmailForm.company_id, state.loginEmailForm.email) ? false : true,
                                code: ''
                            },
                            ContentType.json
                        )
                        .then(async (res) => {
                            switch (res?.code) {
                                case 200:
                                    if (res.data && res.data.token) {
                                        console.log('login success');
                                        await store.dispatch(UserActionTypes.ACTION_LOGIN_EMAIL, {
                                            company_id: state.loginEmailForm.company_id,
                                            email: state.loginEmailForm.email,
                                            remember: false,
                                            token: res.data.token
                                        });
                                        router
                                            .push({
                                                path: state.redirect || '/',
                                                query: state.otherQuery
                                            })
                                            .catch((err) => {
                                                console.log(err);
                                            });
                                    } else {
                                        loginEmailStep.value = 'STEP2';
                                        methods.countDown();
                                    }
                                    break;
                                case 400:
                                    // 参数错误
                                    ElMessage({
                                        message: i18n.global.t('global.error400'),
                                        type: 'error',
                                        duration: 1e3 * 5
                                    });
                                    break;
                                case 30002:
                                    // 发送邮件失败
                                    ElMessage({
                                        message: i18n.global.t('global.error30002'),
                                        type: 'error',
                                        duration: 1e3 * 5
                                    });
                                    break;
                                case 40001:
                                    // 企业号不存在
                                    ElMessage({
                                        message: i18n.global.t('system.companyNotExist'),
                                        type: 'error',
                                        duration: 1e3 * 5
                                    });
                                    break;
                                case 40002:
                                    // 用户不存在
                                    ElMessage({
                                        message: i18n.global.t('system.userNotExist'),
                                        type: 'error',
                                        duration: 1e3 * 5
                                    });
                                    break;
                                case 40003:
                                    // 账号密码错误
                                    ElMessage({
                                        message: i18n.global.t('global.error40003'),
                                        type: 'error',
                                        duration: 1e3 * 5
                                    });
                                    break;
                                case 400031:
                                    // 密码错误，锁定1次
                                    ElMessage({
                                        type: 'error',
                                        duration: 1e3 * 5,
                                        message: i18n.global.t('system.userLockedTip').replace('N', '1')
                                    });
                                    break;
                                case 400032:
                                    // 密码错误，锁定2次
                                    ElMessage({
                                        type: 'error',
                                        duration: 1e3 * 5,
                                        message: i18n.global.t('system.userLockedTip').replace('N', '2')
                                    });
                                    break;
                                case 400033:
                                    // 密码错误，锁定3次
                                    ElMessage({
                                        type: 'error',
                                        duration: 1e3 * 5,
                                        message: i18n.global.t('system.userLockedTip').replace('N', '3')
                                    });
                                    break;
                                case 400034:
                                    // 密码错误，锁定4次
                                    ElMessage({
                                        type: 'error',
                                        duration: 1e3 * 5,
                                        message: i18n.global.t('system.userLockedTip').replace('N', '4')
                                    });
                                    break;
                                case 400035:
                                    // 密码错误，锁定5次
                                    ElMessage({
                                        type: 'error',
                                        duration: 1e3 * 5,
                                        message: i18n.global.t('system.userLockedTip').replace('N', '5')
                                    });
                                    break;
                                case 40007:
                                    // 用户被禁用
                                    ElMessage({
                                        message: i18n.global.t('user.userDisabled'),
                                        type: 'error',
                                        duration: 1e3 * 5
                                    });
                                    break;
                                case 40008:
                                    // 验证码错误
                                    ElMessage({
                                        message: i18n.global.t('global.error40008'),
                                        type: 'error',
                                        duration: 1e3 * 5
                                    });
                                    break;
                                case 40009:
                                    // 发送邮件失败
                                    ElMessage({
                                        message: i18n.global.t('global.error40009'),
                                        type: 'error',
                                        duration: 1e3 * 5
                                    });
                                    break;
                                default:
                                    console.log('===============UNKNOWN==================');
                                    break;
                            }
                        })
                        .catch((err) => {
                            console.warn(err)
                            ElMessage({
                                type: 'error',
                                message: i18n.global.t('system.qa.failRequeat')
                            });
                        });
                });
            },
            verifyEmail: async () => {
                https(false)
                    .request<RootObject<LoginModel>>(
                        'email_login',
                        Method.POST,
                        {
                            email: state.loginEmailForm.email,
                            company_id: state.loginEmailForm.company_id,
                            password: state.loginEmailForm.password,
                            need: true,
                            code: emailVerifyCode.value
                        },
                        ContentType.json
                    )
                    .then(async (res) => {
                        switch (res?.code) {
                            case 200:
                                if (res.data && res.data.token) {
                                    console.log('login success');
                                    await store.dispatch(UserActionTypes.ACTION_LOGIN_EMAIL, {
                                        company_id: state.loginEmailForm.company_id,
                                        email: state.loginEmailForm.email,
                                        remember: isRemember.value,
                                        token: res.data.token
                                    });
                                    router
                                        .push({
                                            path: state.redirect || '/',
                                            query: state.otherQuery
                                        })
                                        .catch((err) => {
                                            console.log(err);
                                        });
                                } else {
                                    console.log(res);
                                }
                                break;
                            case 400:
                                // 参数错误
                                ElMessage({
                                    message: i18n.global.t('global.error400'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            case 500:
                                // 服务器错误
                                ElMessage({
                                    message: i18n.global.t('global.error500'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            case 40001:
                                // 企业号不存在
                                ElMessage({
                                    message: i18n.global.t('system.companyNotExist'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            case 40002:
                                // 用户不存在
                                ElMessage({
                                    message: i18n.global.t('system.userNotExist'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            case 40007:
                                // 用户被禁用
                                ElMessage({
                                    message: i18n.global.t('user.userDisabled'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            case 40008:
                                // 验证码错误
                                ElMessage({
                                    message: i18n.global.t('global.error40008'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            case 40009:
                                // 发送邮件失败
                                ElMessage({
                                    message: i18n.global.t('global.error40009'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            default:
                                console.log('===============UNKNOWN==================');
                                break;
                        }
                    })
                    .catch((err) => {
                        console.warn(err)
                        ElMessage({
                            type: 'error',
                            message: i18n.global.t('system.qa.failRequeat')
                        });
                    });
            },
            reSendEmail: async () => {
                https(false)
                    .request<RootObject<LoginModel>>(
                        'email_login',
                        Method.POST,
                        {
                            email: state.loginEmailForm.email,
                            company_id: state.loginEmailForm.company_id,
                            password: state.loginEmailForm.password,
                            need: true,
                            code: ''
                        },
                        ContentType.json
                    )
                    .then(async (res) => {
                        switch (res?.code) {
                            case 200:
                                // 发送邮件成功
                                loginEmailStep.value = 'STEP2';
                                ElMessage({
                                    message: i18n.global.t('user.tipsresendemail'),
                                    type: 'success',
                                    duration: 1e3 * 5
                                });
                                methods.countDown();
                                break;
                            case 400:
                                // 参数错误
                                ElMessage({
                                    message: i18n.global.t('global.error400'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            case 30002:
                                // 失败
                                ElMessage({
                                    message: i18n.global.t('global.error30002'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            case 40001:
                                // 企业号不存在
                                ElMessage({
                                    message: i18n.global.t('system.companyNotExist'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            case 40002:
                                // 用户不存在
                                ElMessage({
                                    message: i18n.global.t('system.userNotExist'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            case 40003:
                                // 账号密码错误
                                ElMessage({
                                    message: i18n.global.t('global.error40003'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            case 40007:
                                // 用户被禁用
                                ElMessage({
                                    message: i18n.global.t('user.userDisabled'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            case 40008:
                                // 验证码错误
                                ElMessage({
                                    message: i18n.global.t('global.error40008'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            case 40009:
                                // 发送邮件失败
                                ElMessage({
                                    message: i18n.global.t('global.error40009'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            default:
                                console.log('===============UNKNOWN==================');
                                break;
                        }
                    })
                    .catch((err) => {
                        console.warn(err)
                        ElMessage({
                            type: 'error',
                            message: i18n.global.t('system.qa.failRequeat')
                        });
                    });
            },
            countDown() {
                clearInterval(timer);
                leftTime.value = 30;
                countdownTips.value = i18n.global.t('user.emailLogintip2').replace('%d', `<span class="cd">${leftTime.value}</span>`);
                timer = window.setInterval(() => {
                    leftTime.value = leftTime.value - 1;
                    countdownTips.value = i18n.global.t('user.emailLogintip2').replace('%d', `<span class="cd">${leftTime.value}</span>`);
                    if (leftTime.value < 1) {
                        clearInterval(timer);
                    }
                }, 1e3);
            }
        });

        function getOtherQuery(query: LocationQuery) {
            return Object.keys(query).reduce((acc, cur) => {
                if (cur !== 'redirect') {
                    acc[cur] = query[cur];
                }
                return acc;
            }, {} as LocationQuery);
        }

        watch(
            () => route.query,
            (query) => {
                if (query) {
                    state.redirect = query.redirect?.toString() ?? '';
                    state.otherQuery = getOtherQuery(query);
                }
            }
        );
        return {
            userNameRef,
            passwordRef,
            loginFormRef,
            ...toRefs(state),
            ...toRefs(methods),
            // t
            loginMethod,
            loginEmailFormRef,
            loginEmailStep,
            emailVerifyCode,
            isRemember,
            leftTime,
            countdownTips
        };
    }
});
